import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha, Avatar, Box, Grid, LinearProgress, Pagination, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FinanceType, FinanceTypeDef } from "../../../../models/finance/FinanceType";
import { EXPENSES_ROUTE_PATH, INCOMES_ROUTE_PATH } from "../../../../utils/constants/AppConstants";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

export interface MonthlyCategoryBreakdownRow {
  categoryName: string;
  actual: number;
}

interface MonthlyReportsCategoryBreakdownListProps {
  rows: MonthlyCategoryBreakdownRow[];
  type: FinanceTypeDef;
  year: number;
  month: string;
}

const MonthlyReportsCategoryBreakdownListComponent: React.FC<MonthlyReportsCategoryBreakdownListProps> = ({
  rows,
  type,
  year,
  month,
}) => {
  const MOBILE_PAGE_SIZE = 5;
  const DESKTOP_PAGE_SIZE = 7;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { formatCurrencyNumber, formatCompactCurrencyNumber } = useCurrencyFormatter();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const sortedRows = useMemo(() => [...rows].sort((a, b) => b.actual - a.actual), [rows]);
  const total = useMemo(() => sortedRows.reduce((sum, row) => sum + row.actual, 0), [sortedRows]);

  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);

  const palette = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.secondary.main,
    ],
    [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.secondary.main,
    ]
  );

  const handleCategoryClick = (categoryName: string) => {
    const basePath = type === FinanceType.EXPENSE ? EXPENSES_ROUTE_PATH : INCOMES_ROUTE_PATH;
    const queryParams = new URLSearchParams({
      year: String(year),
      month,
      category: categoryName,
    });

    navigate(`${basePath}?${queryParams.toString()}`);
  };

  const pieRows = useMemo(
    () =>
      sortedRows.map((row, index) => ({
        ...row,
        color: palette[index % palette.length],
      })),
    [sortedRows, palette]
  );

  const activeSliceIndex = useMemo(
    () => (activeCategory ? pieRows.findIndex((row) => row.categoryName === activeCategory) : -1),
    [activeCategory, pieRows]
  );

  const activeRow = activeSliceIndex >= 0 ? pieRows[activeSliceIndex] : null;

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [page, pageSize, sortedRows]);

  const pageStart = (page - 1) * pageSize;

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const entry = payload[0]?.payload;
    if (!entry) return null;
    const share = total > 0 ? (entry.actual / total) * 100 : 0;

    return (
      <Box
        sx={{
          p: 1.2,
          borderRadius: 1.5,
          background: alpha(theme.palette.background.paper, 0.97),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          boxShadow: "0 8px 20px rgba(2,6,23,0.45)",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {entry.categoryName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatCurrencyNumber(entry.actual)} ({share.toFixed(1)}%)
        </Typography>
      </Box>
    );
  };

  if (!rows.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No category data yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add transactions to see how categories contribute this month.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              background: alpha(theme.palette.background.default, 0.38),
              p: 1.3,
              height: "100%",
              minHeight: isMobile ? 300 : 370,
              position: "relative",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 12, right: 12, bottom: 12, left: 12 }}>
                <Pie
                  data={pieRows}
                  dataKey="actual"
                  nameKey="categoryName"
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 58 : 78}
                  outerRadius={isMobile ? 96 : 132}
                  paddingAngle={1.5}
                  onMouseEnter={(_, index) => setActiveCategory(pieRows[index]?.categoryName || null)}
                  onMouseLeave={() => setActiveCategory(null)}
                  onClick={(entry) => handleCategoryClick(entry.categoryName)}
                >
                  {pieRows.map((entry, index) => (
                    <Cell
                      key={entry.categoryName}
                      fill={entry.color}
                      opacity={activeSliceIndex === -1 || activeSliceIndex === index ? 1 : 0.35}
                      stroke={activeSliceIndex === index ? alpha(theme.palette.common.white, 0.7) : "transparent"}
                      strokeWidth={activeSliceIndex === index ? 2 : 0}
                    />
                  ))}
                </Pie>
                {!isMobile && <Tooltip content={<CustomTooltip />} />}
              </PieChart>
            </ResponsiveContainer>

            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
                width: isMobile ? 90 : 110,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {activeRow ? activeRow.categoryName : "Total"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {activeRow ? formatCompactCurrencyNumber(activeRow.actual) : formatCompactCurrencyNumber(total)}
              </Typography>
              {(activeRow || total > 0) && (
                <Typography variant="caption" color="text.secondary">
                  {activeRow ? `${((activeRow.actual / total) * 100).toFixed(1)}%` : "100%"}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>
                {pieRows.length} categories
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={1.1}>
            {paginatedRows.map((row, index) => {
              const absoluteIndex = pageStart + index;
              const share = total > 0 ? (row.actual / total) * 100 : 0;
              const color = palette[absoluteIndex % palette.length];

              return (
                <Box
                  key={row.categoryName}
                  onClick={() => handleCategoryClick(row.categoryName)}
                  onMouseEnter={() => setActiveCategory(row.categoryName)}
                  onMouseLeave={() => setActiveCategory(null)}
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    background: alpha(theme.palette.background.default, 0.42),
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    opacity: activeCategory && activeCategory !== row.categoryName ? 0.55 : 1,
                    "&:hover": {
                      borderColor: alpha(color, 0.45),
                      background: alpha(color, 0.09),
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.05} sx={{ mb: 0.65 }}>
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        bgcolor: alpha(color, 0.2),
                        color: color,
                      }}
                    >
                      {absoluteIndex + 1}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.categoryName}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: color, fontWeight: 700 }}>
                      {share.toFixed(1)}%
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={Math.min(share, 100)}
                    sx={{
                      mb: 0.6,
                      height: 6,
                      borderRadius: 999,
                      backgroundColor: alpha(theme.palette.text.primary, 0.08),
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: color,
                        borderRadius: 999,
                      },
                    }}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {formatCompactCurrencyNumber(row.actual)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.9 }}>
                      {formatCurrencyNumber(row.actual)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}

            {pageCount > 1 && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ pt: 0.4 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Showing {pageStart + 1}-{Math.min(pageStart + pageSize, sortedRows.length)} of {sortedRows.length} categories
                </Typography>
                <Pagination
                  count={pageCount}
                  page={page}
                  size="small"
                  onChange={(_, value) => setPage(value)}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: theme.palette.text.secondary,
                    },
                    "& .Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.22),
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MonthlyReportsCategoryBreakdownListComponent;
